import React, { useEffect, useState } from 'react';
import { api } from '../../services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Plus, Trash2, RefreshCcw, Save, Calendar, Banknote, FileText } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (v) => (Number(v) || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 });

const STATO_BADGE = {
  Prevista: 'bg-slate-100 text-slate-700 border-slate-300',
  Pagata: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  Incassata: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  Scaduta: 'bg-red-100 text-red-800 border-red-300',
  Annullata: 'bg-slate-200 text-slate-500 border-slate-300',
};

export default function InvoicePlanDialog({ invoice, open, onOpenChange, enums, onChanged }) {
  const [movements, setMovements] = useState([]);
  const [planForm, setPlanForm] = useState(null);
  const [newMov, setNewMov] = useState({ date: '', amount: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const isClient = invoice && (invoice.direction === 'in' || invoice.direction === 'other_in');

  useEffect(() => {
    if (open && invoice) {
      loadMovements();
      setPlanForm({
        modalita_pagamento: invoice.modalita_pagamento || 'Pagamento unico',
        installments: [],
        anticipata: !!invoice.anticipata,
        anticipo_banca: invoice.anticipo_banca || '',
        anticipo_percentuale: invoice.anticipo_percentuale || 80,
        anticipo_data_accredito: invoice.anticipo_data_accredito || '',
        anticipo_commissioni: invoice.anticipo_commissioni || 0,
        anticipo_interessi: invoice.anticipo_interessi || 0,
      });
      setNewMov({ date: new Date().toISOString().slice(0, 10), amount: '', notes: '' });
    }
  }, [open, invoice]);

  const loadMovements = async () => {
    try {
      const r = await api.get(`/treasury/invoices/${invoice.id}/movements`);
      setMovements(r.data || []);
    } catch { toast.error('Errore caricamento scadenze'); }
  };

  const paidSum = movements.filter(m => m.paid_flag || m.received_flag).reduce((s, m) => s + (m.amount || 0), 0);
  const openSum = movements.filter(m => !m.paid_flag && !m.received_flag).reduce((s, m) => s + (m.amount || 0), 0);
  const totale = invoice?.totale || 0;
  const residuo = totale - paidSum;

  const usesCustom = ['Rate personalizzate', 'Piano di rientro personalizzato'].includes(planForm?.modalita_pagamento);

  const addPlanInstallment = () => setPlanForm(f => ({ ...f, installments: [...(f.installments || []), { date: '', amount: '', notes: '' }] }));
  const updPlanInstallment = (i, k, v) => setPlanForm(f => ({ ...f, installments: f.installments.map((x, idx) => idx === i ? { ...x, [k]: v } : x) }));
  const rmPlanInstallment = (i) => setPlanForm(f => ({ ...f, installments: f.installments.filter((_, idx) => idx !== i) }));

  const handleApplyPlan = async () => {
    if (!window.confirm(`Rigenerare il piano scadenze?\nLe scadenze non ancora pagate verranno sostituite.\nResiduo da pianificare: ${fmt(residuo)}`)) return;
    setSaving(true);
    try {
      const payload = {
        modalita_pagamento: planForm.modalita_pagamento,
        anticipata: !!planForm.anticipata,
      };
      if (planForm.anticipata && isClient) {
        payload.anticipo_banca = planForm.anticipo_banca || null;
        payload.anticipo_percentuale = Number(planForm.anticipo_percentuale);
        payload.anticipo_data_accredito = planForm.anticipo_data_accredito || null;
        payload.anticipo_commissioni = Number(planForm.anticipo_commissioni || 0);
        payload.anticipo_interessi = Number(planForm.anticipo_interessi || 0);
      }
      if (usesCustom) {
        payload.installments = (planForm.installments || []).filter(i => i.date && i.amount).map(i => ({
          date: i.date, amount: Number(i.amount), notes: i.notes || null,
        }));
        if (payload.installments.length === 0) { toast.error('Aggiungi almeno una rata.'); setSaving(false); return; }
        const sum = payload.installments.reduce((s, i) => s + i.amount, 0);
        if (Math.abs(sum - residuo) > 0.5) { toast.error(`Somma rate ${fmt(sum)} ≠ residuo ${fmt(residuo)}`); setSaving(false); return; }
      }
      await api.put(`/treasury/invoices/${invoice.id}/plan`, payload);
      toast.success('Piano aggiornato. Calendario e Concentrazione rigenerati.');
      await loadMovements();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore aggiornamento piano');
    } finally { setSaving(false); }
  };

  const handleMovEdit = async (mov, patch) => {
    try {
      await api.patch(`/treasury/movements/${mov.id}`, patch);
      await loadMovements();
      onChanged && onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const handleMovDelete = async (mov) => {
    if (mov.paid_flag || mov.received_flag) { toast.error('Annulla prima il flag pagamento/incasso.'); return; }
    if (!window.confirm('Eliminare questa scadenza?')) return;
    try {
      await api.delete(`/treasury/movements/${mov.id}`);
      await loadMovements();
      onChanged && onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const handleAddMov = async () => {
    if (!newMov.date || !newMov.amount) { toast.error('Compila data e importo.'); return; }
    try {
      await api.post(`/treasury/invoices/${invoice.id}/movements`, {
        date: newMov.date, amount: Number(newMov.amount), notes: newMov.notes || null,
      });
      toast.success('Rata aggiunta');
      setNewMov({ date: new Date().toISOString().slice(0, 10), amount: '', notes: '' });
      await loadMovements();
      onChanged && onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  if (!invoice || !planForm) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="invoice-plan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Gestione finanziaria fattura
            {invoice.invoice_number && <span className="font-mono text-sm text-slate-500">#{invoice.invoice_number}</span>}
          </DialogTitle>
          <DialogDescription>
            {invoice.party_name || '—'} · {invoice.invoice_date} · Totale {fmt(invoice.totale)}
          </DialogDescription>
        </DialogHeader>

        {/* Summary */}
        <div className="grid grid-cols-4 gap-2">
          <div className="rounded-lg border bg-slate-50 p-2"><p className="text-[10px] uppercase text-slate-500">Totale fattura</p><p className="font-mono font-bold">{fmt(totale)}</p></div>
          <div className="rounded-lg border bg-emerald-50 p-2"><p className="text-[10px] uppercase text-emerald-700">Già {isClient ? 'incassato' : 'pagato'}</p><p className="font-mono font-bold text-emerald-700">{fmt(paidSum)}</p></div>
          <div className="rounded-lg border bg-amber-50 p-2"><p className="text-[10px] uppercase text-amber-700">Aperto previsto</p><p className="font-mono font-bold text-amber-700">{fmt(openSum)}</p></div>
          <div className="rounded-lg border bg-blue-50 p-2"><p className="text-[10px] uppercase text-blue-700">Residuo da pianificare</p><p className="font-mono font-bold text-blue-700">{fmt(residuo)}</p></div>
        </div>

        <Tabs defaultValue="schedule" className="mt-3">
          <TabsList>
            <TabsTrigger value="schedule" data-testid="plan-tab-schedule"><Calendar className="w-4 h-4 mr-1" />Scadenze ({movements.length})</TabsTrigger>
            <TabsTrigger value="modality" data-testid="plan-tab-modality"><RefreshCcw className="w-4 h-4 mr-1" />Modalità pagamento</TabsTrigger>
            {isClient && <TabsTrigger value="anticipo" data-testid="plan-tab-anticipo"><Banknote className="w-4 h-4 mr-1" />Anticipo bancario</TabsTrigger>}
          </TabsList>

          {/* TAB: edit single movements */}
          <TabsContent value="schedule" className="space-y-3">
            <Card><CardContent className="p-0">
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="py-2 px-2">Data</th><th className="py-2 px-2 text-right">Importo</th>
                  <th className="py-2 px-2">Descrizione</th><th className="py-2 px-2">Stato</th><th className="py-2 px-2 w-24"></th>
                </tr></thead>
                <tbody>
                  {movements.length === 0 ? (
                    <tr><td colSpan="5" className="text-center text-slate-400 py-6">Nessuna scadenza</td></tr>
                  ) : movements.map(m => {
                    const locked = m.paid_flag || m.received_flag;
                    return (
                      <tr key={m.id} className={`border-b ${locked ? 'bg-emerald-50/30' : 'hover:bg-slate-50'}`} data-testid={`plan-mov-${m.id}`}>
                        <td className="py-1.5 px-2">
                          <Input key={`date-${m.id}-${m.date}`} type="date" disabled={locked} onBlur={e => e.target.value !== m.date && handleMovEdit(m, { date: e.target.value })} defaultValue={m.date} className="h-7 text-xs" />
                        </td>
                        <td className="py-1.5 px-2">
                          <Input key={`amt-${m.id}-${m.amount}`} type="number" step="0.01" disabled={locked} onBlur={e => Number(e.target.value) !== m.amount && handleMovEdit(m, { amount: Number(e.target.value) })} defaultValue={m.amount} className="h-7 text-xs text-right font-mono" />
                        </td>
                        <td className="py-1.5 px-2 text-xs">{m.description || '—'} {m.is_anticipo && <Badge className="ml-1 bg-blue-50 text-blue-700 border border-blue-200 text-[9px]">Anticipo</Badge>}</td>
                        <td className="py-1.5 px-2"><Badge className={`text-[9px] border ${STATO_BADGE[m.stato] || 'bg-slate-100 border-slate-200'}`}>{m.stato}</Badge></td>
                        <td className="py-1.5 px-2 text-right">
                          <Button size="sm" variant="ghost" disabled={locked} onClick={() => handleMovDelete(m)} title={locked ? 'Scadenza chiusa' : 'Elimina'}>
                            <Trash2 className={`w-3.5 h-3.5 ${locked ? 'text-slate-300' : 'text-red-500'}`} />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent></Card>

            {/* Add ad-hoc installment */}
            <Card className="border-blue-200 bg-blue-50/30"><CardContent className="p-3">
              <p className="text-xs font-semibold mb-2">Aggiungi rata manuale</p>
              <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-3"><Label className="text-[10px]">Data</Label><Input type="date" value={newMov.date} onChange={e => setNewMov({ ...newMov, date: e.target.value })} data-testid="plan-add-date" /></div>
                <div className="col-span-3"><Label className="text-[10px]">Importo</Label><Input type="number" step="0.01" value={newMov.amount} onChange={e => setNewMov({ ...newMov, amount: e.target.value })} data-testid="plan-add-amount" /></div>
                <div className="col-span-4"><Label className="text-[10px]">Note</Label><Input value={newMov.notes} onChange={e => setNewMov({ ...newMov, notes: e.target.value })} /></div>
                <div className="col-span-2"><Button size="sm" onClick={handleAddMov} className="w-full" data-testid="plan-add-btn"><Plus className="w-3 h-3 mr-1" />Aggiungi</Button></div>
              </div>
              <p className="text-[10px] text-slate-500 mt-1.5">Tip: editi data/importo direttamente nella tabella sopra (le scadenze già pagate/incassate sono bloccate).</p>
            </CardContent></Card>
          </TabsContent>

          {/* TAB: change modality (regenerate) */}
          <TabsContent value="modality" className="space-y-3">
            <Card><CardContent className="p-3 space-y-3">
              <p className="text-xs text-slate-600">
                Cambia la modalità di pagamento per <strong>rigenerare le scadenze aperte</strong>.
                Le scadenze già pagate/incassate (<strong>{fmt(paidSum)}</strong>) restano invariate; il residuo di <strong>{fmt(residuo)}</strong> sarà ridistribuito.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Modalità pagamento *</Label>
                  <Select value={planForm.modalita_pagamento} onValueChange={v => setPlanForm({ ...planForm, modalita_pagamento: v })}>
                    <SelectTrigger data-testid="plan-modalita"><SelectValue /></SelectTrigger>
                    <SelectContent>{enums?.modalita_pagamento?.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              {usesCustom && (
                <Card className="border-blue-200 bg-blue-50/30"><CardContent className="p-3 space-y-2">
                  <p className="text-xs font-semibold">Rate personalizzate (somma deve essere {fmt(residuo)})</p>
                  {(planForm.installments || []).map((ins, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-4"><Label className="text-[10px]">Data</Label><Input type="date" value={ins.date} onChange={e => updPlanInstallment(i, 'date', e.target.value)} /></div>
                      <div className="col-span-3"><Label className="text-[10px]">Importo</Label><Input type="number" step="0.01" value={ins.amount} onChange={e => updPlanInstallment(i, 'amount', e.target.value)} /></div>
                      <div className="col-span-4"><Label className="text-[10px]">Note</Label><Input value={ins.notes || ''} onChange={e => updPlanInstallment(i, 'notes', e.target.value)} /></div>
                      <Button type="button" size="sm" variant="ghost" onClick={() => rmPlanInstallment(i)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                    </div>
                  ))}
                  <Button type="button" size="sm" variant="outline" onClick={addPlanInstallment}><Plus className="w-3 h-3 mr-1" />Aggiungi rata</Button>
                </CardContent></Card>
              )}

              <Button onClick={handleApplyPlan} disabled={saving} data-testid="plan-apply-btn"><Save className="w-4 h-4 mr-1" />Applica nuovo piano</Button>
            </CardContent></Card>
          </TabsContent>

          {/* TAB: anticipo bancario (clienti) */}
          {isClient && (
            <TabsContent value="anticipo" className="space-y-3">
              <Card><CardContent className="p-3 space-y-3">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <Checkbox checked={planForm.anticipata} onCheckedChange={v => setPlanForm({ ...planForm, anticipata: !!v })} data-testid="plan-anticipo-check" />
                  Fattura anticipata in banca
                </label>
                {planForm.anticipata ? (
                  <div className="grid grid-cols-3 gap-2">
                    <div><Label className="text-[10px]">Banca</Label><Input value={planForm.anticipo_banca} onChange={e => setPlanForm({ ...planForm, anticipo_banca: e.target.value })} /></div>
                    <div><Label className="text-[10px]">% Anticipo</Label><Input type="number" value={planForm.anticipo_percentuale} onChange={e => setPlanForm({ ...planForm, anticipo_percentuale: e.target.value })} /></div>
                    <div><Label className="text-[10px]">Data accredito</Label><Input type="date" value={planForm.anticipo_data_accredito} onChange={e => setPlanForm({ ...planForm, anticipo_data_accredito: e.target.value })} /></div>
                    <div><Label className="text-[10px]">Commissioni</Label><Input type="number" step="0.01" value={planForm.anticipo_commissioni} onChange={e => setPlanForm({ ...planForm, anticipo_commissioni: e.target.value })} /></div>
                    <div><Label className="text-[10px]">Interessi</Label><Input type="number" step="0.01" value={planForm.anticipo_interessi} onChange={e => setPlanForm({ ...planForm, anticipo_interessi: e.target.value })} /></div>
                  </div>
                ) : (
                  <p className="text-xs text-slate-500">Disattivato: l&apos;incasso seguirà la modalità di pagamento standard.</p>
                )}
                <p className="text-[10px] text-slate-500">
                  L&apos;applicazione delle modifiche genera 2 movimenti: <strong>accredito anticipo</strong> (in data accredito) + <strong>saldo</strong> (alla scadenza naturale).
                </p>
                <Button onClick={handleApplyPlan} disabled={saving} data-testid="plan-apply-anticipo-btn"><Save className="w-4 h-4 mr-1" />Applica modifiche anticipo</Button>
              </CardContent></Card>
            </TabsContent>
          )}
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
